import { debounce } from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";

import timeoutRates from "../../constants/timeoutRates";
import { getScreenPointerPositionFromEvent } from "../../utils/mouseUtils";

const ALLOWED_MOVEMENT = 5;

class LongPressCapture extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.handleStart = this.handleStart.bind(this);
    this.handleMovement = this.handleMovement.bind(this);
    this.cancelLongPress = this.cancelLongPress.bind(this);
    this.cleanupListeners = this.cleanupListeners.bind(this);

    // Using debounce so this even will fire if not cancelled before hand
    this.debouncedHandleLongPress = debounce(this.handleLongPress, timeoutRates.LONG_PRESS_TIME);

    this._startPosition = null;
  }

  componentWillUnmount() {
    this.debouncedHandleLongPress.cancel();
    this.cleanupListeners();
  }

  setupListeners() {
    window.addEventListener("mouseup", this.cancelLongPress, false);
    window.addEventListener("touchend", this.cancelLongPress, false);
    window.addEventListener("mousemove", this.handleMovement, false);
    window.addEventListener("touchmove", this.handleMovement, false);
  }

  cleanupListeners() {
    window.removeEventListener("mouseup", this.cancelLongPress, false);
    window.removeEventListener("touchend", this.cancelLongPress, false);
    window.removeEventListener("mousemove", this.handleMovement, false);
    window.removeEventListener("touchmove", this.handleMovement, false);
  }

  handleStart(e) {
    this._startPosition = getScreenPointerPositionFromEvent(e);
    // If it doesn't get cancelled beforehand, a longPress will trigger
    // after timeout
    this.debouncedHandleLongPress();
    // Setup listeners to cancel the long press
    this.setupListeners();
  }

  handleMovement(e) {
    const pos = getScreenPointerPositionFromEvent(e);
    const dist = this._startPosition
      ? Math.abs(pos.x - this._startPosition.x) + Math.abs(pos.y - this._startPosition.y)
      : 1000;

    if (dist > ALLOWED_MOVEMENT) {
      this.cancelLongPress();
    }
  }

  cancelLongPress() {
    this.debouncedHandleLongPress.cancel();
    this._startPosition = null;
    this.cleanupListeners();
  }

  handleLongPress() {
    this.props.onLongPress();
    this.cleanupListeners();
  }

  render() {
    // Don't wrap it in anything new, just add listeners to what is already there
    const childView = this.props.children;

    // If the child view already contains handlers for what we want to handle,
    // then we need to handle both.
    const onMouseDown = childView.props.onMouseDown
      ? e => {
        this.handleStart(e);
        childView.props.onMouseDown(e);
      }
      : this.handleStart;

    const onTouchStart = childView.props.onTouchStart
      ? e => {
        this.handleStart(e);
        childView.props.onTouchStart(e);
      }
      : this.handleStart;

    return React.cloneElement(
      this.props.children,
      this.props.disabled
        ? {}
        : {
          onTouchStart,
          onMouseDown
        }
    );
  }
}

LongPressCapture.propTypes = {
  children: PropTypes.element.isRequired,
  onLongPress: PropTypes.func,
  disabled: PropTypes.bool
};

export default LongPressCapture;
